/**
 * CheckoutStatusPage — página de retorno post-Stripe (Flow A).
 *
 * Referencia normativa: F4_IMPLEMENTATION_SPECIFICATION_v1.0.md §14.
 *
 * Renderiza los estados terminales SATISFECHO / FRUSTRADO / CANCELADO /
 * CADUCADO / SELLADO consultando el endpoint canónico:
 *   GET /api/intents/{intent_id}/status
 *
 * Polling: cada 3 segundos, hasta que:
 *  - state ∈ {satisfecho, frustrado, cancelado, caducado, sellado} → terminal.
 *  - 5 minutos sin resolución → timeout no terminal (permite volver luego).
 *
 * URL params:
 *  - /checkout/success?intent_id=... — usuario volvió tras completar el pago.
 *  - /checkout/cancel?intent_id=...  — usuario canceló en el checkout.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const TERMINAL_STATES = new Set([
  'satisfecho',
  'frustrado',
  'cancelado',
  'caducado',
  'sellado',
]);

function useIntentStatus(intentId) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedAtRef = useRef(Date.now());
  const cancelledRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!intentId) return null;
    try {
      const res = await axios.get(`${API}/intents/${intentId}/status`);
      return res.data;
    } catch (e) {
      setError(e?.response?.data?.detail?.error_code || 'network_error');
      return null;
    }
  }, [intentId]);

  useEffect(() => {
    if (!intentId) return undefined;
    cancelledRef.current = false;
    startedAtRef.current = Date.now();
    let timer;
    const poll = async () => {
      if (cancelledRef.current) return;
      const data = await fetchOnce();
      if (data) {
        setStatus(data);
        if (TERMINAL_STATES.has(data.state)) {
          return;
        }
      }
      const elapsed = Date.now() - startedAtRef.current;
      if (elapsed >= POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [intentId, fetchOnce]);

  return { status, error, timedOut };
}

function getUrlParam(name) {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

export default function CheckoutStatusPage({ mode }) {
  // mode: 'success' | 'cancel'
  const intentId = getUrlParam('intent_id');
  const { status, error, timedOut } = useIntentStatus(intentId);

  if (!intentId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6">
        <div className="max-w-md text-center space-y-4" data-testid="celula-missing-intent">
          <h1 className="text-2xl font-semibold">Identificador de intento ausente</h1>
          <p className="text-slate-400">
            La URL no incluye <code>intent_id</code>. Regresa al panel y vuelve a intentar.
          </p>
          <a
            href="/"
            data-testid="go-to-dashboard-btn"
            className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded"
          >
            Ir al panel
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6">
      <div className="max-w-lg w-full bg-slate-900 border border-slate-800 rounded-lg p-8 space-y-5 shadow-xl">
        <CheckoutStatusView
          mode={mode}
          intentId={intentId}
          status={status}
          error={error}
          timedOut={timedOut}
        />
        <IntentMetaFooter intentId={intentId} status={status} />
      </div>
    </div>
  );
}

function CheckoutStatusView({ mode, intentId, status, error, timedOut }) {
  // Cancelación forzada por URL (/checkout/cancel) — spec §14: mode='cancel' fuerza el mensaje
  // de cancelación aunque el intent no exista o el status no se pueda consultar.
  if (mode === 'cancel' && !(status && (status.state === 'satisfecho' || status.state === 'sellado'))) {
    return (
      <div className="space-y-3" data-testid="celula-cancel-msg">
        <h2 className="text-xl font-semibold text-yellow-400">Pago cancelado</h2>
        <p className="text-slate-300">
          No se realizó ningún cargo. Puedes reintentar cuando lo desees.
        </p>
        <a
          href="/"
          data-testid="celula-retry-btn"
          className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded"
        >
          Reintentar
        </a>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="space-y-3" data-testid="celula-status-error">
        <h2 className="text-xl font-semibold text-red-400">No pudimos consultar el estado</h2>
        <p className="text-slate-400 text-sm">Código: <code>{error}</code></p>
        <a
          href="/"
          data-testid="go-to-dashboard-btn"
          className="inline-block bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded"
        >
          Volver al panel
        </a>
      </div>
    );
  }

  const state = status?.state;

  // Estado terminal exitoso.
  if (state === 'satisfecho' || state === 'sellado') {
    return (
      <div className="space-y-3" data-testid="celula-activated-msg">
        <h2 className="text-xl font-semibold text-emerald-400">Célula activada</h2>
        <p className="text-slate-300">
          Tu pago fue confirmado y tu Célula está activa. Ya puedes utilizar todos los servicios del ecosistema.
        </p>
        <a
          href="/"
          data-testid="go-to-dashboard-btn"
          className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded"
        >
          Ir al panel
        </a>
      </div>
    );
  }

  // Cancelación explícita por estado del intent.
  if (state === 'cancelado') {
    return (
      <div className="space-y-3" data-testid="celula-cancel-msg">
        <h2 className="text-xl font-semibold text-yellow-400">Pago cancelado</h2>
        <p className="text-slate-300">
          No se realizó ningún cargo. Puedes reintentar cuando lo desees.
        </p>
        <a
          href="/"
          data-testid="celula-retry-btn"
          className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded"
        >
          Reintentar
        </a>
      </div>
    );
  }

  // Fallo definitivo.
  if (state === 'frustrado' || state === 'caducado') {
    return (
      <div className="space-y-3" data-testid="celula-failure-msg">
        <h2 className="text-xl font-semibold text-red-400">
          {state === 'caducado' ? 'La sesión expiró' : 'El pago no pudo completarse'}
        </h2>
        <p className="text-slate-300">
          El intento quedó registrado como <code>{state}</code>. Puedes iniciar un nuevo intento cuando estés listo.
        </p>
        <a
          href="/"
          data-testid="celula-retry-btn"
          className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded"
        >
          Reintentar
        </a>
      </div>
    );
  }

  // Timeout de polling (>5 min sin resolución).
  if (timedOut) {
    return (
      <div className="space-y-3" data-testid="celula-timeout-msg">
        <h2 className="text-xl font-semibold text-slate-200">Verificando pago…</h2>
        <p className="text-slate-400">
          Tu pago está siendo verificado. Puedes cerrar esta ventana y revisar más tarde desde tu panel.
        </p>
        <a
          href="/"
          data-testid="celula-check-later-btn"
          className="inline-block bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded"
        >
          Verificar más tarde
        </a>
      </div>
    );
  }

  // Estado transitorio: emitting / en_curso / validado / awaiting_webhook.
  return (
    <div className="space-y-3" data-testid="celula-emitting-spinner">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <h2 className="text-xl font-semibold text-slate-100">Procesando tu intento…</h2>
      </div>
      <p className="text-slate-400 text-sm">
        Estado actual: <code>{state || 'inicializando'}</code>
        {status?.operation_state ? ` · operación: ${status.operation_state}` : null}
      </p>
    </div>
  );
}

function IntentMetaFooter({ intentId, status }) {
  return (
    <div className="pt-4 border-t border-slate-800 text-xs text-slate-500 space-y-1">
      <div>Intent ID: <code data-testid="celula-intent-id">{intentId}</code></div>
      {status?.magnitude ? (
        <div>Monto: {status.magnitude} {status.canonical_currency}</div>
      ) : null}
      {status?.updated_at ? (
        <div>Actualizado: {new Date(status.updated_at).toLocaleString()}</div>
      ) : null}
    </div>
  );
}
